import { NestedMenuItem } from '@/components/Dropdown/NestedMenuItem';
import { SxProps } from '@mui/material';
import Menu from '@mui/material/Menu';
import * as React from 'react';
import { ReactElement, ReactNode, useEffect } from 'react';

const paperStyles: (width?: string) => SxProps = (width: string = '290px') => ({
    padding: '0 1rem',
    borderRadius: '1.2rem',
    boxShadow: '0px 10px 60px 0px #00000033',
    width,

    '&:focus': {
        outline: 'none',
    },

    'ul li': {
        borderRadius: '1.2rem',
    },
});

interface Item {
    label: ReactElement | null;
    items?: Item[];
}

interface Props {
    width?: string;
    trigger: ReactElement;
    items: Item[];
    renderMenuWrapper?: (children: ReactNode) => ReactNode;
    closed?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    anchorOrigin?: {
        vertical: 'top' | 'bottom' | 'center';
        horizontal: 'left' | 'right' | 'center';
    };
    transformOrigin?: {
        vertical: 'top' | 'bottom' | 'center';
        horizontal: 'left' | 'right' | 'center';
    };
    sx?: SxProps;
}

const defaultRenderMenuWrapper = (children: ReactNode) => <>{children}</>;

const mapItems = ({ items, label }: Item): ReactElement | null => {
    if (items && !label?.props.disabled) {
        return <NestedMenuItem label={label} menu={items.map(mapItems)} />;
    }

    return label;
};

export const Dropdown = React.forwardRef(
    (
        {
            width,
            trigger,
            items,
            renderMenuWrapper = defaultRenderMenuWrapper,
            closed,
            onOpen,
            onClose,
            anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
            transformOrigin = { vertical: 'top', horizontal: 'left' },
            sx,
        }: Props,
        ref: React.ForwardedRef<any> | ((instance: any) => void)
    ) => {
        const [isInternalOpen, setInternalOpen] = React.useState<(EventTarget & HTMLElement) | null>(null);

        useEffect(() => {
            if (closed) {
                setInternalOpen(null);
                onClose?.();
            }
        }, [closed]);

        let anchorRef = React.useRef<any>(null);
        if (ref) {
            if (typeof ref === 'function') {
                ref(anchorRef.current);
            } else if (ref) {
                anchorRef.current = ref;
            }
        }

        const handleOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            event.stopPropagation();

            if (items.length) {
                setInternalOpen(event.currentTarget);
            }
            onOpen?.();
        };

        const handleClose = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            event.stopPropagation();

            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                return;
            }

            handleForceClose();
            onClose?.();
        };

        const handleForceClose = () => {
            setInternalOpen(null);
        };

        const renderMenu = (menuItem: ReactElement | null, index: number): any => {
            if (!menuItem) {
                return;
            }

            const { children, menu, ...props } = menuItem.props;

            return React.createElement(menuItem.type, {
                ...props,
                ...(menu ? { parentMenuOpen: isInternalOpen } : {}),
                key: index,
                children: menu ? menu.map(renderMenu) : children,
            });
        };

        const mappedItems = items.map(mapItems);
        return (
            <>
                {React.cloneElement(trigger, {
                    onClick: isInternalOpen ? handleForceClose : handleOpen,
                    ref: anchorRef,
                })}

                {!closed && (
                    <Menu
                        PaperProps={{ sx: sx ? sx : paperStyles(width) }}
                        anchorEl={isInternalOpen}
                        open={!!isInternalOpen}
                        onClose={handleClose}
                        anchorOrigin={anchorOrigin}
                        transformOrigin={transformOrigin}>
                        {renderMenuWrapper(mappedItems.map(renderMenu))}
                    </Menu>
                )}
            </>
        );
    }
);
